import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { detectSelfHarm } from '@/lib/selfHarmDetection';

export interface ModerationCategories {
  hate_speech: boolean;
  harassment: boolean;
  violence: boolean;
  threats: boolean;
  spam: boolean;
  adult_content: boolean;
  self_harm: boolean;
  misinformation: boolean;
}

export interface PrePublishModerationResult {
  success: boolean;
  moderation: {
    flagged: boolean;
    reason?: string;
    severity?: number;
    confidence?: number;
    categories?: ModerationCategories;
    suggestedAction?: 'approve' | 'review' | 'reject' | 'hold';
  };
  action: 'approved' | 'approved_for_review' | 'held' | 'rejected' | 'self_harm_blocked';
  holdId?: string;
  message: string;
}

interface UsePrePublishModerationOptions {
  onHeld?: (holdId: string) => void;
  onRejected?: (reason: string) => void;
  onSelfHarm?: () => void;
}

export const usePrePublishModeration = (options?: UsePrePublishModerationOptions) => {
  const [isChecking, setIsChecking] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const checkContent = useCallback(async (
    content: string,
    contentType: 'vent' | 'reply' | 'message',
    metadata?: {
      roomId?: string;
      ventId?: string;
      parentReplyId?: string;
    }
  ): Promise<{ canPublish: boolean; result?: PrePublishModerationResult }> => {
    if (!user) {
      return { canPublish: false };
    }

    if (!content.trim()) {
      return { canPublish: false };
    }

    setIsChecking(true);

    try {
      const { data, error } = await supabase.functions.invoke('moderate-content', {
        body: {
          content: content.trim(),
          contentType,
          userId: user.id,
          prePublish: true,
          roomId: metadata?.roomId,
          ventId: metadata?.ventId,
          parentReplyId: metadata?.parentReplyId
        }
      });

      if (error) {
        console.error('Pre-publish moderation error:', error);
        // Fail closed for self-harm even when the check is unavailable
        if (contentType !== 'message' && detectSelfHarm(content)) {
          options?.onSelfHarm?.();
          return { canPublish: false };
        }
        return { canPublish: true };
      }

      const result = data as PrePublishModerationResult;

      // Handle different actions
      switch (result.action) {
        case 'self_harm_blocked':
          options?.onSelfHarm?.();
          return { canPublish: false, result };

        case 'rejected':
          toast({
            variant: 'destructive',
            title: 'Content Not Allowed',
            description: result.message || 'Your content violates our community guidelines.',
          });
          options?.onRejected?.(result.moderation.reason || 'Policy violation');
          return { canPublish: false, result };

        case 'held':
          toast({
            title: 'Content Under Review',
            description: result.message || 'Your content will be reviewed before posting.',
          });
          options?.onHeld?.(result.holdId || '');
          return { canPublish: false, result };

        case 'approved_for_review':
          // Content can be published but will be reviewed
          console.log('Content approved for review:', result.moderation);
          return { canPublish: true, result };

        case 'approved':
        default:
          return { canPublish: true, result };
      }
    } catch (error) {
      console.error('Pre-publish moderation failed:', error);
      if (contentType !== 'message' && detectSelfHarm(content)) {
        options?.onSelfHarm?.();
        return { canPublish: false };
      }
      return { canPublish: true };
    } finally {
      setIsChecking(false);
    }
  }, [user, toast, options]);

  // Post-publish moderation (for logging after content is created)
  const logPublishedContent = useCallback(async (
    content: string,
    contentType: 'vent' | 'reply' | 'message',
    contentId: string
  ) => {
    if (!user) return;

    try {
      await supabase.functions.invoke('moderate-content', {
        body: {
          content: content.trim(),
          contentType,
          contentId,
          userId: user.id,
          prePublish: false
        }
      });
    } catch (error) {
      console.error('Post-publish moderation logging failed:', error);
    }
  }, [user]);

  return {
    checkContent,
    logPublishedContent,
    isChecking
  };
};
